import { trackEvent } from "@/amplitudeClient";
import { AMPLITUDE_EVENT, EVENT, trackGAEvent } from "@/utils/analytics";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Image from "src/components/image";

// import { useStableNavigateCallback } from "@/hooks/useStableNavigateCallback";
import Lottie from "lottie-react";

import { useModelViewerStore } from "src/store/modelViewerStore";

interface ReconstructionSliderProps {
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  currentNavMode?: string;
}

export default function ReconstructionSlider({
  min = 0,
  max = 1,
  step = 0.001,
  className = "",
  currentNavMode,
}: ReconstructionSliderProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const progress = useModelViewerStore((state) => state.progress);
  const setProgress = useModelViewerStore((s) => s.setProgress);

  const clampValue = (v: number) => Math.min(max, Math.max(min, v));
  const percent = useMemo(() => {
    if (max <= min) return 0;
    return ((progress - min) / (max - min)) * 100;
  }, [progress, min, max]);

  const quantize = (v: number) => {
    const steps = Math.round((v - min) / step);
    return clampValue(min + steps * step);
  };

  const getIconPath = (code: string) => `/assets/icon_dark_${code}.png`;

  const handleValueChange = useCallback(
    (newValue: number) => {
      setProgress(newValue);
    },
    [setProgress],
  );

  useEffect(() => {
    const handleMove = (clientX: number) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
      const ratio = rect.width > 0 ? x / rect.width : 0;
      const raw = min + ratio * (max - min);
      handleValueChange(quantize(raw));
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
      console.log("onTouchMove: isDragging=", isDragging);
      if (!isDragging) return;
      if (e.touches.length > 0) handleMove(e.touches[0].clientX);
    };

    const stop = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", stop);
      window.addEventListener("touchmove", onTouchMove as any, {
        passive: false,
      });
      window.addEventListener("touchend", stop);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onTouchMove as any);
      window.removeEventListener("touchend", stop);

      console.log("nav slider cleanup end");
    };
  }, [isDragging, min, max, step, handleValueChange]);

  const startDragMouse = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };
  const startDragTouch = (e: React.TouchEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };
  const jumpTo = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clickX = Math.min(rect.width, Math.max(0, e.clientX - rect.left));
    const ratio = rect.width > 0 ? clickX / rect.width : 0;
    const raw = min + ratio * (max - min);
    handleValueChange(quantize(raw));
  };

  return (
    <div className={`flex w-full select-none ${className} px-[12px]`}>
      <div
        ref={trackRef}
        className="relative h-4 w-full rounded-full slider-bg"
        onMouseDown={jumpTo}
        onClick={jumpTo}
      >
        {/* start cap */}
        <div
          className={`pointer-events-none absolute left-[5px] top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full slider-cap flex items-center justify-center ${
            percent > 0 ? "slider-filled" : ""
          }`}
        >
          <span className="cap-color h-3 w-3 rounded-full" />
        </div>
        {/* end cap */}
        <div className="pointer-events-none absolute -right-[40px] top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full slider-cap flex items-center justify-center">
          <span className="cap-color h-3 w-3 rounded-full" />
        </div>
        {/* filled track */}
        <div
          className="absolute left-0 top-0 h-[16px] rounded-full slider-filled"
          style={{ width: `${percent}%` }}
        />
        {/* thumb */}
        <button
          className="slider-thumb absolute -top-3 h-9 w-9 -translate-x-1/2 cursor-pointer"
          style={{ left: `${percent}%` }}
          onMouseDown={startDragMouse}
          onTouchStart={startDragTouch}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={progress}
          aria-label="Progress"
        >
          <div className="flex h-10 w-10 items-center justify-center slider-filled rounded-full backdrop-blur">
            {currentNavMode ? (
              <Image
                aria-hidden
                src={getIconPath(currentNavMode)}
                alt="thumb icon"
                width={20}
                height={20}
              />
            ) : (
              <></>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}
